import { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2, MessageSquare, ShieldCheck, HelpCircle } from 'lucide-react';

export default function ContactUs() {
  const { colors } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiryType: 'General Inquiry',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    // Simulate server response
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        inquiryType: 'General Inquiry',
        subject: '',
        message: ''
      });
    }, 800);
  };

  const contactInfo = [
    {
      icon: <Mail size={22} />,
      label: 'Email Support',
      value: 'support@metaqode.co.in',
      detail: 'Quick responses within 24 hours',
      href: 'mailto:support@metaqode.co.in',
      action: 'Send Email'
    },
    {
      icon: <Phone size={22} />,
      label: 'Customer Care',
      value: '+91 9980998199',
      detail: 'Mon - Sun: 8:00 AM - 10:00 PM',
      href: 'tel:+919980998199',
      action: 'Call Us'
    },
    {
      icon: <MapPin size={22} />,
      label: 'Corporate Office',
      value: 'MetaQode Technologies Pvt. Ltd.',
      detail: 'Chitradurga - 577501, Karnataka, India',
      href: 'https://maps.google.com/?q=Chitradurga+Karnataka+India',
      action: 'View Map'
    },
  ];

  const faqs = [
    {
      q: 'How do I book a sports venue on TurfX?',
      a: 'Navigate to the Explore page, search or filter by your city/sport, pick an available slot time, and complete the instant payment online.'
    },
    {
      q: 'Can I cancel or reschedule my turf booking?',
      a: 'Yes, cancellations or slot rescheduling are allowed as per the respective venue’s cancellation policy shown during checkout and in your My Bookings tab.'
    },
    {
      q: 'How can venue owners partner with TurfX?',
      a: 'Click on "Partner with Us" in the footer or top navigation to sign up for a Partner account and start listing your grounds right away.'
    },
    {
      q: 'What payment options are supported?',
      a: 'We support all major payment modes including UPI (Google Pay, PhonePe, Paytm), Credit/Debit Cards, Net Banking, and PayU wallet options.'
    },
    {
      q: 'What happens if rain interrupts my booked slot?',
      a: 'Rain check policies are determined by individual venue managers. You can contact venue management directly via your booking details.'
    }
  ];

  const inpStyle = {
    width: '100%',
    padding: '14px 16px',
    borderRadius: '12px',
    border: `1.5px solid ${colors.border}`,
    background: colors.background,
    color: colors.text,
    fontSize: '0.95rem',
    fontWeight: '500',
    outline: 'none',
    transition: 'border-color 0.2s',
    WebkitAppearance: 'none'
  };

  return (
    <div style={{ minHeight: '100vh', background: colors.background, color: colors.text }}>
      {/* Hero Section */}
      <section style={{
        padding: '6rem 1.5rem 5rem',
        background: 'linear-gradient(135deg, #084734 0%, #0a5c44 50%, #063527 100%)',
        position: 'relative', overflow: 'hidden', textAlign: 'center'
      }}>
        <div style={{
          position: 'absolute', top: '-40%', left: '-15%', width: '500px', height: '500px',
          borderRadius: '50%', background: 'rgba(206, 241, 123, 0.07)', pointerEvents: 'none',
          WebkitBackdropFilter: 'blur(30px)', backdropFilter: 'blur(30px)'
        }} />
        <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            padding: '6px 18px', borderRadius: '50px', background: 'rgba(206, 241, 123, 0.15)',
            color: '#CEF17B', fontSize: '0.85rem', fontWeight: '700', marginBottom: '1.25rem',
            border: '1px solid rgba(206, 241, 123, 0.3)'
          }}>
            <MessageSquare size={16} /> 24/7 Dedicated Support
          </div>
          <h1 style={{
            fontSize: 'clamp(2.2rem, 5vw, 3.5rem)', fontWeight: '900', color: '#fff',
            marginBottom: '1.25rem', lineHeight: 1.25, letterSpacing: '-0.02em'
          }}>
            We're Here to <span style={{ color: '#CEF17B' }}>Help You Play</span>
          </h1>
          <p style={{
            fontSize: 'clamp(1rem, 2vw, 1.15rem)', color: 'rgba(255,255,255,0.85)',
            lineHeight: 1.8, maxWidth: '600px', margin: '0 auto'
          }}>
            Have questions about booking a venue, listing your ground, or general inquiries? 
            Get in touch with our team today.
          </p>
        </div>
      </section>

      {/* Contact Cards Section */}
      <section style={{
        maxWidth: '1100px', margin: '-2.5rem auto 4rem', padding: '0 1.5rem',
        position: 'relative', zIndex: 2
      }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '1.5rem'
        }}>
          {contactInfo.map((info) => (
            <div key={info.label} style={{
              background: colors.cardBg, borderRadius: '24px', padding: '2rem 1.8rem',
              border: `1.5px solid ${colors.border}`, display: 'flex', flexDirection: 'column',
              justify: 'space-between', boxShadow: `0 10px 30px ${colors.shadow || 'rgba(0,0,0,0.05)'}`,
              transition: 'transform 0.25s ease'
            }}>
              <div>
                <div style={{
                  width: '52px', height: '52px', borderRadius: '16px', background: '#DCEFB8',
                  color: '#084734', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '1.25rem'
                }}>
                  {info.icon}
                </div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '800', color: colors.text, marginBottom: '0.4rem' }}>
                  {info.label}
                </h3>
                <p style={{ fontSize: '1.02rem', fontWeight: '700', color: '#084734', marginBottom: '0.4rem' }}>
                  {info.value}
                </p>
                <p style={{ fontSize: '0.85rem', color: colors.textSecondary, marginBottom: '1.5rem' }}>
                  {info.detail}
                </p>
              </div>

              {info.href && (
                <a href={info.href} target={info.href.startsWith('http') ? '_blank' : '_self'} rel="noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    padding: '10px 20px', borderRadius: '12px', background: '#084734', color: '#CEF17B',
                    fontWeight: '700', fontSize: '0.88rem', textDecoration: 'none', transition: 'all 0.2s'
                  }}>
                  {info.action}
                </a>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Form & FAQ Container */}
      <section style={{ padding: '0 1.5rem 5rem', maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '3rem', alignItems: 'start'
        }}>
          {/* Contact Form */}
          <div style={{
            background: colors.cardBg, borderRadius: '28px', padding: '2.5rem 2rem',
            border: `1.5px solid ${colors.border}`, boxShadow: `0 12px 35px ${colors.shadow || 'rgba(0,0,0,0.05)'}`
          }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '900', color: colors.text, marginBottom: '0.5rem' }}>
              Send Us a <span style={{ color: '#084734' }}>Message</span>
            </h2>
            <p style={{ fontSize: '0.92rem', color: colors.textSecondary, marginBottom: '1.8rem' }}>
              Fill out the form below and our customer support team will respond promptly.
            </p>

            {submitted ? (
              <div style={{
                background: '#DCEFB8', borderRadius: '20px', padding: '2.5rem 1.5rem',
                textAlign: 'center', border: '1.5px solid #CEF17B'
              }}>
                <CheckCircle2 size={54} color="#084734" style={{ margin: '0 auto 1rem' }} />
                <h3 style={{ fontSize: '1.25rem', fontWeight: '900', color: '#084734', marginBottom: '0.5rem' }}>
                  Message Sent Successfully!
                </h3>
                <p style={{ fontSize: '0.92rem', color: '#084734', lineHeight: 1.6, marginBottom: '1.5rem' }}>
                  Thank you for contacting TurfX. Our support team will review your message and reach back within 24 hours.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  style={{
                    padding: '12px 28px', borderRadius: '50px', background: '#084734',
                    color: '#CEF17B', fontWeight: '800', border: 'none', cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}>
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', marginBottom: '0.4rem', color: colors.text }}>
                      Your Name *
                    </label>
                    <input
                      type="text" required placeholder="John Doe"
                      style={inpStyle}
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', marginBottom: '0.4rem', color: colors.text }}>
                      Phone Number
                    </label>
                    <input
                      type="tel" placeholder="+91 9980998199"
                      style={inpStyle}
                      value={formData.phone}
                      onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', marginBottom: '0.4rem', color: colors.text }}>
                    Email Address *
                  </label>
                  <input
                    type="email" required placeholder="john@example.com"
                    style={inpStyle}
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', marginBottom: '0.4rem', color: colors.text }}>
                    Inquiry Type
                  </label>
                  <select
                    style={inpStyle}
                    value={formData.inquiryType}
                    onChange={e => setFormData({ ...formData, inquiryType: e.target.value })}
                  >
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Turf Partner Onboarding">Turf Partner Onboarding</option>
                    <option value="Booking & Slot Support">Booking & Slot Support</option>
                    <option value="Billing & Refunds">Billing & Refunds</option>
                    <option value="Feedback & Suggestions">Feedback & Suggestions</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', marginBottom: '0.4rem', color: colors.text }}>
                    Subject *
                  </label>
                  <input
                    type="text" required placeholder="Brief topic of your message"
                    style={inpStyle}
                    value={formData.subject}
                    onChange={e => setFormData({ ...formData, subject: e.target.value })}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', marginBottom: '0.4rem', color: colors.text }}>
                    Message *
                  </label>
                  <textarea
                    required rows={4} placeholder="How can we assist you today?"
                    style={{ ...inpStyle, resize: 'vertical' }}
                    value={formData.message}
                    onChange={e => setFormData({ ...formData, message: e.target.value })}
                  />
                </div>

                <button
                  type="submit" disabled={submitting}
                  style={{
                    padding: '16px', borderRadius: '14px', background: '#084734',
                    color: '#CEF17B', fontWeight: '800', fontSize: '1rem', border: 'none',
                    cursor: submitting ? 'not-allowed' : 'pointer', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', gap: '10px',
                    boxShadow: '0 8px 25px rgba(8,71,52,0.25)', transition: 'all 0.2s'
                  }}>
                  {submitting ? 'Sending Message...' : <><Send size={18} /> Send Message</>}
                </button>
              </form>
            )}
          </div>

          {/* FAQs Column */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.25rem' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '12px', background: '#DCEFB8',
                color: '#084734', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <HelpCircle size={22} />
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '900', color: colors.text, margin: 0 }}>
                Frequently Asked <span style={{ color: '#084734' }}>Questions</span>
              </h2>
            </div>
            
            <p style={{ fontSize: '0.92rem', color: colors.textSecondary, marginBottom: '1.8rem' }}>
              Got quick questions? Check out our most frequently asked queries below.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {faqs.map((faq, idx) => (
                <details key={idx} style={{
                  background: colors.cardBg, borderRadius: '18px',
                  border: `1.5px solid ${colors.border}`, overflow: 'hidden',
                  transition: 'border-color 0.2s'
                }}>
                  <summary style={{
                    padding: '1.2rem 1.4rem', fontSize: '0.95rem',
                    fontWeight: '800', color: colors.text, cursor: 'pointer',
                    listStyle: 'none', display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', userSelect: 'none'
                  }}>
                    {faq.q}
                    <span style={{ fontSize: '1.2rem', color: '#084734', fontWeight: '900', marginLeft: '10px' }}>+</span>
                  </summary>
                  <div style={{
                    padding: '0 1.4rem 1.2rem', fontSize: '0.9rem',
                    color: colors.textSecondary, lineHeight: 1.75
                  }}>
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
